import { cn } from '@repo/tailwind-config/tailwind.functions'
import Button from '@repo/ui/button'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { FieldValues, useFormContext } from 'react-hook-form'

import { ValidationButton } from '../../lib/utils/buttons-config/validation-buttons-config'

export interface FormRedirectionPaths {
  returnPath: string
  createPath: string
}

export interface ValidationButtonsBaseFormProps<T> {
  buttonsConfig: ValidationButton<T>[]
  preventSubmit?: boolean
}

type Props<T> = ValidationButtonsBaseFormProps<T> & {
  hasBorderTop?: boolean
  preComponent?: React.ReactNode
}

const ValidationButtons = <T extends FieldValues>({
  buttonsConfig,
  hasBorderTop,
  preventSubmit = false,
  preComponent
}: Props<T>) => {
  const [loading, setLoading] = useState<Record<number, boolean>>({})

  const form = useFormContext<T>()
  const queryClient = useQueryClient()
  const isButtonDisabled = Object.values(loading).some((value) => value)

  const handleButtonClick = async (action: (values: T) => void, button: keyof typeof loading) => {
    if (preventSubmit) return

    // Set loading state to true for the button
    setLoading((prev) => ({ ...prev, [button]: true }))

    try {
      // Trigger form validation before submitting
      const isValid = await form.trigger() // Explicitly trigger validation

      if (!isValid) {
        await form.handleSubmit(action, console.log)()
        return // Exit if form is not valid
      }

      // Submit the form with the provided action
      await form.handleSubmit(action, console.log)()
    } catch (error) {
      // Handle any unexpected errors during form submission
      console.error('An error occurred during form submission:', error)
    } finally {
      // Clear mutation cache after submission
      queryClient.getMutationCache().clear()

      // Set loading state back to false for the button
      setLoading((prev) => ({ ...prev, [button]: false }))
    }
  }

  return (
    <div
      className={cn('flex justify-end w-full gap-3 pt-5', {
        'border-t border-t-white-active p-5 mt-5': hasBorderTop
      })}
    >
      {preComponent}
      {buttonsConfig.map((button, index) => (
        <Button
          key={index}
          type={'button'}
          icon={button.icon}
          label={button.label}
          variant={button.variant || 'primary'}
          loading={loading[index] || button.isLoading}
          disabled={isButtonDisabled || button.disabled}
          className={button.className}
          onClick={() => {
            return !!button.bypassValidation
              ? button.action()
              : handleButtonClick(button.action, index)
          }}
        />
      ))}
    </div>
  )
}

export default ValidationButtons
